import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import "../assets/css/ViewCustomer.css";
import "../assets/css/EditTenant.css";
import "../assets/css/Projects.css";
import axiosInstance from "../components/api/axiosInstance";
import UserStoriesTable from "./UserStoriesTable";
import SubProjectFileAttachments from "./SubProjectFileAttachments";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";

function ViewSubproject({ onBack, onEditSubproject }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId } = useParams();
  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canEditSubproject = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const [subproject, setSubproject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usersRoleMap, setUsersRoleMap] = useState({});

  useEffect(() => {
    const fetchSubproject = async () => {
      if (!subprojectId) return;
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/sub-projects/${subprojectId}`);
        const raw = response.data?.data ?? response.data;
        const sp = raw && (raw.Sub_Project_Id != null || raw.sub_project_id != null) ? raw : null;
        setSubproject(sp || null);
        if (!sp) setError("Subproject not found.");
      } catch (err) {
        console.error("Error fetching subproject:", err);
        setError(err.response?.data?.message || "Failed to load subproject.");
        setSubproject(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSubproject();
  }, [subprojectId]);

  useEffect(() => {
    const fetchUsersForRoles = async () => {
      try {
        const response = await axiosInstance.get("/users");
        const arr = Array.isArray(response.data)
          ? response.data
          : response.data?.data ?? response.data?.users ?? [];
        const map = {};
        (arr || []).forEach((u) => {
          const id = u.user_id ?? u.id ?? u.User_Id;
          if (id == null) return;
          const roleDisplay =
            (u.role ?? u.role_name ?? u.Role ?? u.Role_Name ?? "").toString().trim() ||
            (Array.isArray(u.roles) && u.roles.length > 0
              ? u.roles.map((r) => r.role_name ?? r.roleName ?? r.name ?? r.role ?? "").filter(Boolean).join(", ")
              : "");
          map[String(id)] = (roleDisplay || "").trim() || "—";
        });
        setUsersRoleMap(map);
      } catch (err) {
        console.error("Error fetching users for roles:", err);
        setUsersRoleMap({});
      }
    };
    fetchUsersForRoles();
  }, [subprojectId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else navigate(`/projects/${projectId}/view`);
  };

  const handleEdit = () => {
    if (onEditSubproject && projectId && subprojectId) {
      onEditSubproject(projectId, subprojectId);
    } else {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/edit`);
    }
  };

  const refreshSubproject = useCallback(async () => {
    if (!subprojectId) return;
    try {
      const response = await axiosInstance.get(`/sub-projects/${subprojectId}`);
      const raw = response.data?.data ?? response.data;
      const next = raw && (raw.Sub_Project_Id != null || raw.sub_project_id != null) ? raw : null;
      if (next) setSubproject(next);
    } catch (err) {
      console.error("Error refetching subproject:", err);
    }
  }, [subprojectId]);

  const InfoItem = ({ label, value }) => {
    const hasValue = value != null && value !== "" && value !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading subproject...</div>
      </div>
    );
  }

  if (error || !subproject) {
    return (
      <div className="viewlead-container">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-message" style={{ backgroundColor: "#fee", color: "#c33", padding: "12px", borderRadius: "4px", marginTop: "16px" }}>
          {error || "Subproject not found."}
        </div>
      </div>
    );
  }

  const sp = subproject;

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" className="btn-subproject-primary" onClick={handleEdit}>
          Edit Subproject
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "stretch",
          flexWrap: "nowrap",
          marginBottom: 20
        }}
      >
        <div
          className="viewlead-card"
          style={{ flex: "2 1 0", minWidth: 260 }}
        >
          <h3 className="viewlead-section-title">Subproject Information</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Subproject ID" value={String(sp.Sub_Project_Id ?? sp.sub_project_id ?? "")} />
            <InfoItem label="Subproject Name" value={sp.Sub_Project_Name || sp.sub_project_name} />
            <InfoItem label="Short Code" value={sp.Short_Code || sp.short_code} />
            <InfoItem label="Project" value={sp.Project_Name || sp.project_name} />
            <InfoItem label="Status" value={sp.Status || sp.status} />
            <InfoItem label="Visibility" value={sp.Visibility || sp.visibility} />
            <InfoItem label="Domain" value={sp.Domain || sp.domain} />
            <InfoItem label="Category" value={sp.Category || sp.category} />
            <InfoItem label="Start Date" value={formatDate(sp.Start_Date || sp.start_date)} />
            <InfoItem label="End Date" value={formatDate(sp.End_Date || sp.end_date)} />
            <InfoItem label="Actual Start Date" value={formatDate(sp.Actual_Start_Date || sp.actual_start_date)} />
            <InfoItem label="Actual End Date" value={formatDate(sp.Actual_End_Date || sp.actual_end_date)} />
          </div>

          <h3 className="viewlead-section-title">Description</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Description" value={sp.Description || sp.description} />
          </div>
          <h3 className="viewlead-section-title">Notes</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Notes" value={sp.Notes || sp.notes} />
          </div>
        </div>

        <div
          className="viewlead-card"
          style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480 }}
        >
          <h3 className="viewlead-section-title">Team Members</h3>
          <div className="viewlead-info-grid">
            {(sp.Users || sp.users || []).length > 0 ? (
              <div style={{ gridColumn: "1 / -1" }}>
                {(sp.Users || sp.users).map((u) => {
                  const userId = u.User_Id ?? u.user_id;
                  const roleDisplay = usersRoleMap[String(userId)] ?? u.Role_Name ?? u.role_name ?? u.Role ?? u.role ?? "—";
                  return (
                    <div key={u.User_Id || u.user_id} style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {u.User_Name || u.user_name || "—"}
                      </span>
                      <span style={{ color: "#888", marginLeft: 8 }}>
                        ({roleDisplay})
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <InfoItem label="Team Members" value="No team members assigned" />
            )}
          </div>

          <h3 className="viewlead-section-title">Audit</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InfoItem label="Created By" value={sp.Created_By_Name || sp.created_by_name} />
            <InfoItem label="Created On" value={formatDate(sp.Created_On || sp.created_on)} />
            <InfoItem label="Updated By" value={sp.Updated_By_Name || sp.updated_by_name} />
            <InfoItem label="Updated On" value={formatDate(sp.Updated_On || sp.updated_on)} />
          </div>
        </div>
      </div>

      <div className="viewlead-card issue-attachments-card project-attachments-card">
        <SubProjectFileAttachments
          subproject={sp}
          subprojectId={subprojectId}
          canUpload={canEditSubproject}
          title="Attachments"
          onRefresh={refreshSubproject}
        />
      </div>

      <div className="viewlead-card project-subprojects-view-card">
        <UserStoriesTable
          cardTitle="User Stories"
          storiesScope="subproject"
          projectId={projectId}
          subProjectId={subprojectId}
          projectName={sp.Project_Name || sp.project_name}
          subProjectName={sp.Sub_Project_Name || sp.sub_project_name}
        />
      </div>
    </div>
  );
}

export default ViewSubproject;

